import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart3, Eye, Loader2, MousePointerClick, RefreshCw } from 'lucide-react';
import {
  engagementRate,
  formatEngagementWhen,
  loadAutomationStepsEngagement,
  type CampaignEngagementStats,
} from '../../lib/emailEngagement';
import {
  contentModeBadgeClass,
  contentModeBadgeLabel,
  inferContentMode,
} from './contentMode';
import type { AutomationStepRow } from './types';
import ClicksByLinkTable from '../Newsletters/ClicksByLinkTable';

function describeSchedule(step: AutomationStepRow): string {
  const dayPart =
    step.day_offset === 0
      ? 'Same day'
      : step.day_offset === 1
        ? 'Next day'
        : `+${step.day_offset}d`;
  return `${dayPart} · ${step.time_of_day}`;
}

function stepLabel(step: AutomationStepRow): string {
  return step.name?.trim() || step.subject?.trim() || `Step ${step.position}`;
}

interface AutomationStepEngagementTabProps {
  steps: AutomationStepRow[];
  automationTz: string;
}

export default function AutomationStepEngagementTab({
  steps,
  automationTz,
}: AutomationStepEngagementTabProps) {
  const [statsByStep, setStatsByStep] = useState<Map<string, CampaignEngagementStats>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);

  const activeSteps = useMemo(
    () => [...steps].filter((s) => s.active).sort((a, b) => a.position - b.position),
    [steps],
  );

  const load = useCallback(async () => {
    if (activeSteps.length === 0) {
      setStatsByStep(new Map());
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const next = await loadAutomationStepsEngagement(activeSteps.map((s) => s.id));
      setStatsByStep(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load engagement');
      setStatsByStep(new Map());
    } finally {
      setLoading(false);
    }
  }, [activeSteps]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = useMemo(() => {
    let sent = 0;
    let opens = 0;
    let clickers = 0;
    let clicks = 0;
    for (const stats of statsByStep.values()) {
      sent += stats.recipientsSent;
      opens += stats.uniqueOpens;
      clickers += stats.uniqueClickers;
      clicks += stats.totalClicks;
    }
    return { sent, opens, clickers, clicks };
  }, [statsByStep]);

  const hasAnyTracking = useMemo(() => {
    for (const stats of statsByStep.values()) {
      if (
        stats.source !== 'none' ||
        stats.uniqueOpens > 0 ||
        stats.uniqueClickers > 0
      ) {
        return true;
      }
    }
    return false;
  }, [statsByStep]);

  return (
    <section className="bg-white dark:bg-gray-800 min-w-0 overflow-hidden sm:rounded-xl sm:shadow sm:border sm:border-gray-200 sm:dark:border-gray-700">
      <div className="px-4 sm:px-6 py-2.5 sm:py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <BarChart3 className="w-4 h-4 text-emerald-500 shrink-0" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
              Step engagement
            </h2>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 shrink-0"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        <p className="mt-2 hidden sm:block text-sm text-gray-600 dark:text-gray-300">
          Open and click rates per step across all enrollments. Last open/click times use Postmark{' '}
          <span className="font-medium">ReceivedAt</span> in{' '}
          <span className="font-medium">{automationTz}</span> (not send time).
        </p>
      </div>

      {activeSteps.length === 0 ? (
        <div className="px-4 sm:px-6 py-10 text-sm text-gray-500 dark:text-gray-400 text-center">
          No active steps. Add steps on the Overview tab to measure engagement per step.
        </div>
      ) : loading && statsByStep.size === 0 ? (
        <div className="px-4 sm:px-6 py-10 flex justify-center">
          <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
        </div>
      ) : error ? (
        <div className="px-4 sm:px-6 py-4 text-sm text-red-700 dark:text-red-300">{error}</div>
      ) : (
        <>
          {totals.sent > 0 ? (
            <div className="px-4 sm:px-6 py-3 border-b border-gray-100 dark:border-gray-700/80 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Sends (all steps)
                </div>
                <div className="font-semibold text-gray-900 dark:text-white tabular-nums">
                  {totals.sent.toLocaleString()}
                </div>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                  One per subscriber per step — not unique people
                </p>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Opens
                </div>
                <div className="font-semibold text-gray-900 dark:text-white tabular-nums">
                  {totals.opens.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Clickers
                </div>
                <div className="font-semibold text-gray-900 dark:text-white tabular-nums">
                  {totals.clickers.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Total clicks
                </div>
                <div className="font-semibold text-gray-900 dark:text-white tabular-nums">
                  {totals.clicks.toLocaleString()}
                </div>
              </div>
            </div>
          ) : null}

          {!hasAnyTracking && totals.sent > 0 ? (
            <p className="mx-4 sm:mx-6 mt-4 text-xs text-gray-500 dark:text-gray-400 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2">
              No opens or clicks recorded yet. Tracking applies to sends after Postmark open/link
              tracking was enabled.
            </p>
          ) : null}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Step
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">
                    Sent
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                    Opens
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">
                    Open rate
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                    Clickers
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">
                    Click rate
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                    Clicks
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {activeSteps.map((step) => {
                  const stats = statsByStep.get(step.id);
                  const sent = stats?.recipientsSent ?? 0;
                  const opens = stats?.uniqueOpens ?? 0;
                  const clickers = stats?.uniqueClickers ?? 0;
                  const clicks = stats?.totalClicks ?? 0;
                  const mode = inferContentMode(step);
                  const expanded = expandedStepId === step.id;
                  const linkRows = stats?.linkClicks ?? [];
                  const hasExpandableDetails =
                    linkRows.length > 0 || !!stats?.lastOpenAt || !!stats?.lastClickAt;

                  return (
                    <Fragment key={step.id}>
                      <tr
                        className={
                          hasExpandableDetails
                            ? 'cursor-pointer hover:bg-gray-50/80 dark:hover:bg-gray-900/30'
                            : ''
                        }
                        onClick={() => {
                          if (!hasExpandableDetails) return;
                          setExpandedStepId(expanded ? null : step.id);
                        }}
                      >
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {stepLabel(step)}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {describeSchedule(step)}
                          </div>
                          <span
                            className={`inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${contentModeBadgeClass(mode)}`}
                          >
                            {contentModeBadgeLabel(mode)}
                          </span>
                          {stats?.source === 'rollup' ? (
                            <span className="ml-1 text-[10px] text-gray-400 dark:text-gray-500">
                              · frozen
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-800 dark:text-gray-200">
                          {sent}
                        </td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-800 dark:text-gray-200">
                          {opens}
                        </td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums font-medium text-sky-700 dark:text-sky-300">
                          {engagementRate(opens, sent)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-800 dark:text-gray-200">
                          {clickers}
                        </td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums font-medium text-violet-700 dark:text-violet-300">
                          {engagementRate(clickers, sent)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-800 dark:text-gray-200">
                          {clicks}
                          {hasExpandableDetails ? (
                            <span className="block text-[10px] text-gray-400 font-normal">
                              {expanded
                                ? 'hide details'
                                : linkRows.length > 0
                                  ? `${linkRows.length} link${linkRows.length === 1 ? '' : 's'}`
                                  : 'see more'}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                      {expanded && hasExpandableDetails ? (
                        <tr className="bg-gray-50/80 dark:bg-gray-900/40">
                          <td colSpan={7} className="px-4 py-3">
                            {(stats?.lastOpenAt || stats?.lastClickAt) && (
                              <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600 dark:text-gray-300 mb-3">
                                {stats.lastOpenAt ? (
                                  <div className="flex items-center gap-1.5">
                                    <dt className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400">
                                      <Eye className="w-3 h-3" />
                                      Last open
                                    </dt>
                                    <dd className="tabular-nums text-gray-800 dark:text-gray-200">
                                      {formatEngagementWhen(stats.lastOpenAt, automationTz)}
                                    </dd>
                                  </div>
                                ) : null}
                                {stats.lastClickAt ? (
                                  <div className="flex items-center gap-1.5">
                                    <dt className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400">
                                      <MousePointerClick className="w-3 h-3" />
                                      Last click
                                    </dt>
                                    <dd className="tabular-nums text-gray-800 dark:text-gray-200">
                                      {formatEngagementWhen(stats.lastClickAt, automationTz)}
                                    </dd>
                                  </div>
                                ) : null}
                              </dl>
                            )}
                            {linkRows.length > 0 ? (
                              <ClicksByLinkTable
                                linkClicks={linkRows}
                                filter={{ automationStepId: step.id }}
                                canViewClickers={stats?.source === 'live'}
                                timeZone={automationTz}
                                limit={8}
                                title={`Clicks by link — ${stepLabel(step)}`}
                                cellPaddingClass="px-0"
                                compact
                              />
                            ) : null}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-4 sm:px-6 py-3 border-t border-gray-100 dark:border-gray-700/80 flex flex-wrap gap-3 text-[11px] text-gray-400 dark:text-gray-500">
            <span className="inline-flex items-center gap-1">
              <Eye className="w-3 h-3" /> Unique opens per sent email
            </span>
            <span className="inline-flex items-center gap-1">
              <MousePointerClick className="w-3 h-3" /> Unique clickers per sent email
            </span>
          </div>
        </>
      )}
    </section>
  );
}
